import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  afterRenderEffect,
  inject,
  signal,
  viewChild,
} from '@angular/core';

/** Delay before the tooltip shows, so passing the pointer over a row doesn't flash it. */
const SHOW_DELAY_MS = 300;
/** Gap between the cell text and the tooltip (the arrow sits in it). */
const GAP = 8;
/** Smallest distance kept from the viewport edges. */
const EDGE = 8;

interface Tip {
  readonly text: string;
  /** The truncated element's box in viewport coordinates. */
  readonly anchor: DOMRect;
}

/**
 * Figma Tooltip (AUR Libraries 186:115, inverted with arrow) for table text
 * cut off with an ellipsis: hovering or focusing such text shows its full
 * value. One instance in the app shell serves every table, and only text
 * that is actually truncated gets a tooltip. It is drawn fixed to the
 * viewport because table cells clip their content (`overflow: hidden`), and
 * it closes on pointer leave, blur, scroll or resize.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-truncation-tooltip',
  standalone: true,
  template: `
    @if (tip(); as t) {
      <div #bubble class="ttip" role="tooltip" data-testid="truncation-tooltip">
        {{ t.text }}
      </div>
    }
  `,
  styles: `
    .ttip {
      position: fixed;
      top: 0;
      left: 0;
      z-index: 1000;
      max-width: min(360px, calc(100vw - 16px));
      padding: var(--padding-6, 6px) var(--padding-8, 8px);
      font: var(--type-label-sm);
      color: var(--ink-on-solid);
      white-space: normal;
      overflow-wrap: anywhere;
      background: var(--fill-neutral-solid);
      border-radius: var(--radius-sm);
      box-shadow: var(--shadow-overlay);
      pointer-events: none;
      visibility: hidden;
    }
    .ttip::before {
      content: '';
      position: absolute;
      left: var(--arrow-x, 50%);
      width: 8px;
      height: 8px;
      background: var(--fill-neutral-solid);
      border-radius: 1px;
      transform: translateX(-50%) rotate(45deg);
    }
    .ttip[data-placement='top']::before {
      bottom: -4px;
    }
    .ttip[data-placement='bottom']::before {
      top: -4px;
    }
  `,
})
export class TruncationTooltipComponent {
  private readonly bubble = viewChild<ElementRef<HTMLElement>>('bubble');

  readonly tip = signal<Tip | null>(null);
  private current: HTMLElement | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Place the bubble once it is rendered and measurable: centred over the
    // text, kept inside the viewport, flipped below when there is no room.
    afterRenderEffect(() => {
      const tip = this.tip();
      const el = this.bubble()?.nativeElement;
      if (!tip || !el) return;
      const { width, height } = el.getBoundingClientRect();
      const centre = tip.anchor.left + tip.anchor.width / 2;
      const left = Math.min(Math.max(EDGE, centre - width / 2), window.innerWidth - EDGE - width);
      const above = tip.anchor.top - GAP - height >= EDGE;
      const top = above ? tip.anchor.top - GAP - height : tip.anchor.bottom + GAP;
      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
      el.style.setProperty('--arrow-x', `${centre - left}px`);
      el.dataset['placement'] = above ? 'top' : 'bottom';
      el.style.visibility = 'visible';
    });
    const onScroll = () => this.hide();
    document.addEventListener('scroll', onScroll, true);
    inject(DestroyRef).onDestroy(() => {
      document.removeEventListener('scroll', onScroll, true);
      this.clearTimer();
    });
  }

  @HostListener('document:mouseover', ['$event'])
  @HostListener('document:focusin', ['$event'])
  protected onEnter(event: Event): void {
    const truncated = truncatedTableText(event.target);
    if (truncated === this.current) return;
    this.hide();
    if (!truncated) return;
    this.current = truncated;
    this.timer = setTimeout(() => {
      this.timer = null;
      const text = truncated.innerText.trim();
      if (text && truncated.isConnected) {
        this.tip.set({ text, anchor: truncated.getBoundingClientRect() });
      }
    }, SHOW_DELAY_MS);
  }

  @HostListener('document:mouseout', ['$event'])
  protected onLeave(event: MouseEvent): void {
    const next = event.relatedTarget as Node | null;
    if (this.current && !(next && this.current.contains(next))) this.hide();
  }

  @HostListener('document:focusout')
  @HostListener('window:resize')
  protected hide(): void {
    this.clearTimer();
    this.current = null;
    if (this.tip()) this.tip.set(null);
  }

  private clearTimer(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }
}

/**
 * The element whose text is cut off with an ellipsis, at or above `node`
 * and within its table cell; null outside tables or when nothing is cut off.
 */
export function truncatedTableText(node: EventTarget | null): HTMLElement | null {
  if (!(node instanceof Element)) return null;
  const cell = node.closest('td, th');
  if (!cell || !cell.closest('table')) return null;
  for (let el: Element | null = node; el; el = el.parentElement) {
    if (
      el instanceof HTMLElement &&
      getComputedStyle(el).textOverflow === 'ellipsis' &&
      el.scrollWidth > el.clientWidth + 1
    ) {
      return el;
    }
    if (el === cell) break;
  }
  return null;
}
